/**
 * React Hooks for Supabase Teams with SQL Server Fallback
 * Provides easy-to-use hooks for fetching and managing team data
 * Falls back to SQL Server if Supabase data is not available
 */

import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { 
  teamService, 
  SupabaseTeam, 
  TeamFilters, 
  PaginationOptions, 
  TeamResponse 
} from '@/integrations/supabase/teamService';
import { useTeams as useSqlServerTeams } from '@/hooks/useTeams';

/**
 * Hook to fetch teams with filtering and pagination
 * Falls back to SQL Server if Supabase has no data
 */
export function useSupabaseTeams(
  filters: TeamFilters = {},
  pagination: PaginationOptions = {},
  enabled: boolean = true
): UseQueryResult<TeamResponse, Error> {
  // Try Supabase first
  const supabaseQuery = useQuery({
    queryKey: ['supabase-teams', filters, pagination],
    queryFn: () => teamService.getTeams(filters, pagination),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1, // Only retry once, then fallback
  });

  // Fallback to SQL Server if Supabase fails or has no data
  const sqlServerQuery = useSqlServerTeams(filters, enabled && (!supabaseQuery.data?.data?.length || supabaseQuery.isError));

  // Transform SQL Server data to match Supabase format
  const transformedSqlServerData = sqlServerQuery.teams?.map(team => ({
    id: team.ID,
    legacy_id: team.ID,
    team_name: team.TeamName,
    description: team.Descrt,
    report_name_prefix: team.ReportNamePrefix,
    manager_users: team.ManagerUsers,
    member_users: team.MemberUsers,
    unit_id: team.UnitID,
    comp_id: team.CompID,
    sync_source: 'sql_server',
    synced_at: new Date().toISOString(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  })) || [];

  // Return appropriate response
  if (supabaseQuery.data?.data?.length) {
    // Supabase has data, use it
    return supabaseQuery;
  } else if (sqlServerQuery.teams?.length) {
    // Fallback to SQL Server data
    return {
      ...supabaseQuery,
      data: {
        data: transformedSqlServerData,
        count: transformedSqlServerData.length,
        page: 1,
        limit: pagination.limit || 50,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false,
      },
      isLoading: sqlServerQuery.loading,
      error: sqlServerQuery.error,
      isError: !!sqlServerQuery.error,
    } as UseQueryResult<TeamResponse, Error>;
  } else {
    // Return Supabase query (even if empty)
    return supabaseQuery;
  }
}

/**
 * Hook to fetch a single team by ID
 */
export function useSupabaseTeam(
  id: string,
  enabled: boolean = true
): UseQueryResult<SupabaseTeam | null, Error> {
  return useQuery({
    queryKey: ['supabase-team', id],
    queryFn: () => teamService.getTeamById(id),
    enabled: enabled && !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
}

/**
 * Hook to fetch a single team by legacy ID (SQL Server ID)
 */
export function useSupabaseTeamByLegacyId(
  legacyId: string,
  enabled: boolean = true
): UseQueryResult<SupabaseTeam | null, Error> {
  return useQuery({
    queryKey: ['supabase-team-legacy', legacyId],
    queryFn: () => teamService.getTeamByLegacyId(legacyId),
    enabled: enabled && !!legacyId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
}

/**
 * Hook to fetch distinct units
 */
export function useTeamUnits(): UseQueryResult<string[], Error> {
  return useQuery({
    queryKey: ['team-units'],
    queryFn: () => teamService.getUnits(),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook to fetch distinct companies
 */
export function useTeamCompanies(): UseQueryResult<string[], Error> {
  return useQuery({
    queryKey: ['team-companies'],
    queryFn: () => teamService.getCompanies(),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook to fetch teams statistics
 */
export function useTeamsStats(): UseQueryResult<{
  totalTeams: number;
  uniqueUnits: number;
  uniqueCompanies: number;
  teamsWithManagers: number;
  latestSyncDate: string | null;
}, Error> {
  return useQuery({
    queryKey: ['teams-stats'],
    queryFn: () => teamService.getTeamsStats(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook to search teams
 */
export function useSearchTeams(
  searchTerm: string,
  pagination: PaginationOptions = {},
  enabled: boolean = true
): UseQueryResult<TeamResponse, Error> {
  return useQuery({
    queryKey: ['search-teams', searchTerm, pagination],
    queryFn: () => teamService.searchTeams(searchTerm, pagination),
    enabled: enabled && searchTerm.trim().length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch teams by unit
 */
export function useTeamsByUnit(
  unitId: string,
  pagination: PaginationOptions = {},
  enabled: boolean = true
): UseQueryResult<TeamResponse, Error> {
  return useSupabaseTeams(
    { unit_id: unitId },
    pagination,
    enabled && !!unitId
  );
}

/**
 * Hook to fetch teams by company
 */
export function useTeamsByCompany(
  compId: string,
  pagination: PaginationOptions = {},
  enabled: boolean = true
): UseQueryResult<TeamResponse, Error> {
  return useSupabaseTeams(
    { comp_id: compId },
    pagination,
    enabled && !!compId
  );
}

/**
 * Default export with commonly used hooks
 */
export default {
  useSupabaseTeams,
  useSupabaseTeam,
  useSupabaseTeamByLegacyId,
  useTeamUnits,
  useTeamCompanies,
  useTeamsStats,
  useSearchTeams,
  useTeamsByUnit,
  useTeamsByCompany,
};